import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Input, Select, Space, Table, Typography } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import api from '@/services/api.service';
import recognitionService, { CONTRACT_STATUSES, type ContractListRow } from '@/services/financeRecognition.service';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import { StatusTag, accountLabel, amount, apiErrorMessage, contractPath, humanize, options, useRecognitionAccess } from './prepaidDisplay';

const { Title } = Typography;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

const PrepaidContractsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const access = useRecognitionAccess();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');

  const filters = {
    status: params.get('status') ?? undefined,
    category_id: params.get('category_id') ?? undefined,
    supplier_id: params.get('supplier_id') ?? undefined,
    source_type: params.get('source_type') ?? undefined,
    prepaid_asset_account_id: params.get('prepaid_asset_account_id') ?? undefined,
    search: params.get('search') ?? undefined,
    page: Number(params.get('page') || 1),
    limit: Number(params.get('limit') || 25),
    sort: params.get('sort') ?? undefined,
    order: params.get('order') ?? undefined,
  };

  const setFilter = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!('page' in patch)) next.delete('page');
    setParams(next);
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'contracts', filters],
    queryFn: () => recognitionService.listContracts(filters),
    placeholderData: keepPreviousData,
    enabled: access.canView,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['recognition', 'categories'],
    queryFn: () => recognitionService.listCategories(),
    enabled: access.canView,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-list-prepaid'],
    queryFn: async () => unwrap<any[]>(await api.get('/procurement/suppliers')) ?? [],
  });
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);

  const statusValue = useMemo(() => (filters.status ? filters.status.split(',') : []), [filters.status]);

  if (!access.canView) {
    return <Alert type="warning" showIcon message={t('noAccessPrepaidContracts')} style={{ margin: 24 }} />;
  }

  const onTableChange = (p: TablePaginationConfig, _f: unknown, sorter: SorterResult<ContractListRow> | SorterResult<ContractListRow>[]) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    setFilter({
      page: String(p.current ?? 1),
      limit: String(p.pageSize ?? 25),
      sort: s?.order ? String(s.field) : undefined,
      order: s?.order ? (s.order === 'ascend' ? 'ASC' : 'DESC') : undefined,
    });
  };

  const sortOrder = (field: string) => (filters.sort === field ? (filters.order === 'ASC' ? 'ascend' : 'descend') : null);

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/prepaid')}>
            {t('workspace')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t('prepaidContractsTitle')}
          </Title>
        </Space>
        {access.canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/prepaid/new')}>
            {t('newPrepaid')}
          </Button>
        )}
      </Space>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder={t('searchContractsPlaceholder')}
            style={{ width: 320 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={(v) => setFilter({ search: v || undefined })}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder={tc('status')}
            style={{ minWidth: 220 }}
            value={statusValue}
            options={options(CONTRACT_STATUSES)}
            onChange={(v: string[]) => setFilter({ status: v.length ? v.join(',') : undefined })}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('category')}
            style={{ width: 200 }}
            value={filters.category_id}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            onChange={(v) => setFilter({ category_id: v })}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('supplier')}
            style={{ width: 220 }}
            value={filters.supplier_id}
            options={suppliers.map((s: any) => ({ value: s.id, label: s.supplier_name }))}
            onChange={(v) => setFilter({ supplier_id: v })}
          />
          <Select
            allowClear
            placeholder={t('source')}
            style={{ width: 170 }}
            value={filters.source_type}
            options={options(['PURCHASE_INVOICE', 'MANUAL', 'OPENING_BALANCE', 'ADDITION'])}
            onChange={(v) => setFilter({ source_type: v })}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('prepaidAccount')}
            style={{ width: 240 }}
            value={filters.prepaid_asset_account_id}
            options={assetAccounts.map((a) => ({ value: a.id, label: accountLabel(a.account_code, a.account_name) }))}
            onChange={(v) => setFilter({ prepaid_asset_account_id: v })}
          />
        </Space>
      </Card>

      {isError && <Alert type="error" showIcon message={apiErrorMessage(error)} style={{ marginBottom: 12 }} />}

      <Table<ContractListRow>
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.data ?? []}
        onChange={onTableChange}
        scroll={{ x: 1500 }}
        pagination={{
          current: data?.page ?? filters.page,
          pageSize: data?.limit ?? filters.limit,
          total: data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (t) => `${t} contracts`,
        }}
        columns={[
          {
            title: t('number'),
            dataIndex: 'recognition_number',
            width: 150,
            fixed: 'left',
            sorter: true,
            sortOrder: sortOrder('recognition_number'),
            render: (v: string, r) => <Link to={contractPath(r.id)}>{v}</Link>,
          },
          { title: t('description'), dataIndex: 'description', ellipsis: true, width: 240 },
          { title: t('supplier'), dataIndex: 'supplier_name', ellipsis: true, width: 180 },
          { title: t('category'), dataIndex: 'category_name', width: 140 },
          { title: t('source'), dataIndex: 'source_type', width: 140, render: (v: string, r) => r.source_document_number || humanize(v) },
          { title: t('service'), key: 'service', width: 200, render: (_: unknown, r) => `${r.service_start_date} → ${r.service_end_date}` },
          {
            title: t('original'),
            dataIndex: 'recognizable_amount',
            align: 'right',
            width: 150,
            render: (v: string, r) => amount(v, r.transaction_currency),
          },
          { title: t('recognised'), dataIndex: 'recognized_amount', align: 'right', width: 130, render: (v: string) => amount(v) },
          {
            title: t('remaining'),
            dataIndex: 'remaining_amount',
            align: 'right',
            width: 130,
            sorter: true,
            sortOrder: sortOrder('remaining_amount'),
            render: (v: string) => amount(v),
          },
          { title: t('prepaidGl'), key: 'pa', width: 180, ellipsis: true, render: (_: unknown, r) => accountLabel(r.prepaid_account_code, r.prepaid_account_name) },
          { title: t('expenseGl'), key: 'ea', width: 180, ellipsis: true, render: (_: unknown, r) => accountLabel(r.expense_account_code, r.expense_account_name) },
          {
            title: tc('status'),
            dataIndex: 'status',
            width: 140,
            fixed: 'right',
            sorter: true,
            sortOrder: sortOrder('status'),
            render: (s: string) => <StatusTag status={s} />,
          },
        ]}
      />
    </div>
  );
};

export default PrepaidContractsPage;
